/**
 * useCapacitorDeepLinks — listen for native deep-link openings and forward
 * the path to react-router.
 *
 * Batch 48 — wires up the existing `/i/accept/:token` invite route to
 * work when an email-invite link is tapped from outside the app on
 * iOS/Android. The same listener will pick up future deep-link routes
 * (event/activity/whereabouts) for free.
 *
 * Strategy: on URL open, strip the scheme + host and feed the pathname
 * (+ search) into react-router via `navigate()`. Same code-path as if
 * the user typed the URL in the address bar — no special handling per
 * route.
 *
 * Web: this hook no-ops via the Capacitor.isNativePlatform() guard.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

export function useCapacitorDeepLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    let listener;
    let cancelled = false;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import("@capacitor/app");
        listener = await App.addListener("appUrlOpen", (event) => {
          // event.url is the full URL the user tapped, e.g.
          //   https://kinly.geminii-ai.com/i/accept/abc123
          //   kinly://i/accept/abc123
          try {
            const url = new URL(event.url);
            // Custom-scheme URLs (`kinly://`) parse with host = first path
            // segment — re-construct the pathname carefully.
            const path = url.protocol.startsWith("http")
              ? `${url.pathname}${url.search}${url.hash}`
              : `/${url.host}${url.pathname}${url.search}${url.hash}`;
            if (!cancelled && path && path !== "/") navigate(path);
          } catch (e) {
            console.warn("deep link parse failed", event.url, e);
          }
        });
      } catch (e) {
        // Capacitor not installed on web — silently ignore.
        console.debug("deep link listener skipped:", e?.message);
      }
    })();
    return () => {
      cancelled = true;
      if (listener && typeof listener.remove === "function") listener.remove();
    };
  }, [navigate]);
}
